import Link from 'next/link'
import { useRouter } from 'next/router'
import { Trans, t } from '@lingui/macro'
import clsx from 'clsx'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline'

const IndexPagination = ({
  page = 1,
  totalPages = 1,
  type = 'news',
  variant = '',
  year = null,
  month = null,
}) => {
  const router = useRouter()

  const currentPage = parseInt(page)

  const linkParams = (pageNum) => {
    const query = { ...router.query, page: pageNum }
    if (router.pathname !== '/search/page/[page]' && query['type']) {
      delete query['type']
    }

    if (pageNum === 1) {
      delete query['page']
      return {
        href: {
          pathname: `/${type}${variant !== '' ? `/${variant}` : ``}${
            year !== null ? `/${year}${month !== null ? `/${month}` : ``}` : ``
          }`,
          query,
        },
      }
    }
    return {
      href: {
        pathname: `/${type}${variant !== '' ? `/${variant}` : ``}${
          year !== null ? `/${year}${month !== null ? `/${month}` : ``}` : ``
        }/page/[page]`,
        query,
      },
    }
  }

  const pageLink = (pageNum, { disabled = false, label = false } = {}) => {
    const params = linkParams(pageNum)
    if (disabled) {
      return (
        <span className="relative z-10 flex h-10 items-center rounded-md border border-primary-100 bg-white px-2 text-sm font-medium text-primary-200">
          {label ? (
            label
          ) : (
            <span
              className={`block${pageNum.toString().length < 3 ? ` px-2` : ``}`}
            >
              {pageNum}
            </span>
          )}
        </span>
      )
    }
    return (
      <Link {...params}>
        <a
          aria-label={t`Go to page ${pageNum}`}
          aria-current={pageNum === currentPage}
          className={clsx(
            'relative z-10 flex h-10 items-center rounded-md border px-2 text-sm font-medium',
            pageNum === currentPage
              ? 'border-accent-200 bg-white  text-accent-500'
              : 'border-primary-200 bg-white  text-primary-500'
          )}
        >
          {label ? (
            label
          ) : (
            <span
              className={`block${pageNum.toString().length < 3 ? ` px-2` : ``}`}
            >
              {pageNum}
            </span>
          )}
        </a>
      </Link>
    )
  }

  const pages = () => {
    const items = []
    let left = currentPage
    let right = currentPage
    if (totalPages > 1 && currentPage > 1) {
      left = currentPage - 1
    }
    if (totalPages > currentPage + 1) {
      right = currentPage + 1
    }
    if (totalPages > 3 && currentPage < 3) {
      left = 2
      right = 4
    }
    if (totalPages > 3 && currentPage > totalPages - 3) {
      left = totalPages - 3
    }
    if (totalPages < 5) {
      left = 1
    }
    if (totalPages > 2 && totalPages < 5) {
      right = totalPages
    }
    if (totalPages > 5 && currentPage > 3) {
      items.push(
        <li className="hidden md:block" key={`page-left-break`}>
          <span>...</span>
        </li>
      )
    }

    for (let i = left; i <= right; i++) {
      items.push(<li key={`page-link-${i}`}>{pageLink(i)}</li>)
    }
    if (totalPages > 5 && right < totalPages - 1) {
      items.push(
        <li className="hidden md:block" key={`page-right-break`}>
          <span>...</span>
        </li>
      )
    }
    return items
  }

  const nextLabel = (
    <>
      <span className="hidden md:block">
        <Trans>Next</Trans>
      </span>
      <ChevronRightIcon className="mx-2 block h-4 w-4 md:mr-0" />
    </>
  )

  const prevLabel = (
    <>
      <ChevronLeftIcon className="mx-2 block h-4 w-4 md:ml-0" />
      <span className="hidden md:block">
        <Trans>Previous</Trans>
      </span>
    </>
  )

  return (
    <div className="py-4 sm:px-6">
      <nav role="navigation" aria-label="Pagination Navigation">
        <ul className="flex flex-wrap items-center justify-center gap-1">
          <li>
            {pageLink(currentPage - 1, {
              disabled: currentPage < 2,
              label: prevLabel,
            })}
          </li>
          {totalPages > 4 && (
            <li className={currentPage > 3 ? `hidden md:block` : `block`}>
              {pageLink(1)}
            </li>
          )}
          {pages()}
          {totalPages > 4 && (
            <li
              className={
                currentPage < totalPages - 3 ? `hidden md:block` : `block`
              }
            >
              {pageLink(totalPages)}
            </li>
          )}
          <li>
            {pageLink(currentPage + 1, {
              disabled: currentPage >= totalPages,
              label: nextLabel,
            })}
          </li>
        </ul>
      </nav>
    </div>
  )
}

export default IndexPagination
